import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Grid, Typography, Box, Stack } from '@mui/material'
import { useTheme } from '@mui/styles'
import SimpleMaterialsTable from 'components/generic/printable/table/SimpleMaterialsTable'
import ReceiptGridMetaInfo from 'components/generic/printable/components/ReceiptGridMetaInfo'
import { setDecimalPrecision } from 'utils/helpers'
import { PAYMENT_METHODS_VALUES, TICKET_STATUSES } from 'utils/constants'

const PrintablePurchaseFull = ({
  printInboundTicketData,
  ticketCreator,
  paymentData,
  pricePrepend,
  customerData,
  vehicleData,
}) => {
  const {
    customer,
    effectiveDate,
    transportationInfo,
    materials,
    address,
    carrierName,
    taxRate,
    freightCost,
    notes,
    netValue,
    roundingDelta,
    status,
    truckWeight,
    feeOrPromotion,
    feeOrPromotionTotal,
  } = printInboundTicketData
  const { paymentMethod, checkNumber, signatureImageData, fingerprintImageData, paymentDate } = paymentData
  const theme = useTheme()
  const formattedSubTotal = setDecimalPrecision(netValue, { shouldFormatNumber: false, decimalPrecisionOverride: 2 })
  const roundedSubTotal =
    parseFloat(formattedSubTotal) + parseFloat(roundingDelta || 0) + parseFloat(feeOrPromotionTotal || 0)
  const chargeback = freightCost?.chargeBack || 0
  const { enablePrintedDocCompliance, useMinimalObt } = useSelector(state => state.featureFlags?.variables || {})

  const imageStyle = {
    color: theme.palette.text.hint,
    height: 120,
    width: 120,
    objectFit: 'contain',
  }

  const { settings } = useSelector(state => state.settings)
  const { includeOperatorLastName } = settings?.documentSettings?.general || {}

  const formattedMaterials = useMemo(
    () =>
      materials
        ?.map(material => ({
          ...material,
          materialName: material?.overrideName || material?.materialName || material?.materialType?.commonName,
          overrideName: '',
        }))
        ?.sort((a, b) => {
          if (!a.rowIndex) return 1
          if (!b.rowIndex) return -1
          return a.rowIndex - b.rowIndex
        }) || [],
    [materials],
  )

  return (
    <>
      <ReceiptGridMetaInfo
        customer={customer}
        ticketCreator={ticketCreator}
        carrierName={carrierName}
        dispatchTicket={printInboundTicketData?.linkedDispatchTicket?.externalId}
        effectiveDate={effectiveDate}
        paymentDate={paymentDate}
        transportationInfo={transportationInfo}
        notes={notes}
        type="PRINT_PURCHASE_TICKET"
        paymentMethod={PAYMENT_METHODS_VALUES[paymentMethod?.toUpperCase()]}
        checkNumber={checkNumber}
        inboundTicketAddress={address}
        licenseNumber={customerData?.contacts?.[0]?.licenseNumber}
        plateNumber={vehicleData?.plateNumber}
      />

      {formattedMaterials.length > 0 && (
        <Grid sx={useMinimalObt ? { flex: 1, border: '1px solid black', borderTop: '0px', padding: 1 } : {}} container>
          {useMinimalObt ? (
            <Grid item xs={12}>
              <strong>Order Information</strong>
            </Grid>
          ) : (
            <Typography mt={3} variant="body1">
              Materials
            </Typography>
          )}
          <SimpleMaterialsTable
            materials={formattedMaterials}
            truckWeight={truckWeight}
            taxRate={taxRate}
            subTotalOverride={roundedSubTotal}
            type="PRINT_PURCHASE_TICKET"
            showMaterialDeductionNotes
            isPrint
            chargeback={chargeback}
            pricePrepend={pricePrepend}
            feesAndPromotions={
              feeOrPromotion?.map(item => ({
                amount: item.amount,
                amountType: item.amountType,
                eligiblePaymentTypes: item.eligiblePaymentTypes,
                showPaymentMethodSpecificIcon:
                  status?.value === TICKET_STATUSES.PAID.id ? false : item.eligiblePaymentTypes?.length < 4,
                id: item.settingId,
                name: item.name,
                netValue: item.calculatedValue,
                rowType: item.type.toLowerCase(),
              })) || []
            }
          />
        </Grid>
      )}

      {(signatureImageData || fingerprintImageData) && (
        <Grid container sx={useMinimalObt ? { border: '1px solid black', borderTop: '0px', padding: 1 } : {}}>
          <Stack direction="row" justifyContent="flex-start" sx={{ marginTop: useMinimalObt ? 0 : 5 }}>
            {signatureImageData ? (
              <Stack
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                  width: '50%',
                  marginRight: '50px',
                }}
              >
                <img
                  src={signatureImageData}
                  alt="signature"
                  style={{
                    ...imageStyle,
                    width: '100%',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    width: '100%',
                    paddingTop: '5px',
                    borderTop: `1px solid ${theme.palette.borderColor.default}`,
                    fontSize: '8px',
                  }}
                >
                  Customer Signature
                </Typography>
              </Stack>
            ) : null}
            <Stack alignItems="center">
              {fingerprintImageData ? (
                <img
                  src={fingerprintImageData}
                  alt="fingerprint"
                  style={{
                    ...imageStyle,
                    height: 200,
                  }}
                />
              ) : null}
            </Stack>
          </Stack>
        </Grid>
      )}

      {useMinimalObt ? (
        <>
          <Grid container sx={{ border: '1px solid black', borderTop: '0px', padding: 1 }}>
            <Grid item xs={12}>
              <strong>WeighMaster</strong>
              <Typography variant="body2">{`${ticketCreator.firstName}${includeOperatorLastName ? ` ${ticketCreator.lastName}` : ''}`}</Typography>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} py={1} pl={2} sx={{ border: '1px solid black', borderTop: '0px', padding: 1 }}>
              <strong>Disclaimer</strong>
              <Typography variant="body2" fontSize=".6rem">
                {settings?.documentSettings?.general?.purchaseTicketDisclaimer || ''}
              </Typography>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Box
            sx={{
              marginTop: 5,
              borderBottom: `1px solid ${theme.palette.borderColor.default}`,
            }}
          >
            <Grid container sx={{ width: '100%' }}>
              <Grid flex={1}>
                <Typography
                  variant="body1"
                  sx={{
                    paddingBottom: '5px',
                    marginRight: 5,
                  }}
                >
                  Accepted&emsp;&emsp;&emsp;[&emsp;&emsp;]
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <div id="disclaimer">
            <Box mt={2}>
              <Typography variant="body1">Disclaimer</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '8px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {settings?.documentSettings?.general?.purchaseTicketDisclaimer || ''}
              </Typography>
            </Box>
            <Box mt={2} sx={{ borderBottom: '1px dotted lightgray' }} />
          </div>
        </>
      )}

      {enablePrintedDocCompliance && !useMinimalObt && (
        <div id="weighmaster-certificate">
          <Box mt={2}>
            <Typography variant="body1">WEIGHMASTER CERTIFICATE</Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '8px',
              }}
            >
              {settings?.documentSettings?.general?.weighmasterCertificate || ''}
            </Typography>
          </Box>
          <Box mt={2} sx={{ borderBottom: '1px dotted lightgray' }} />
        </div>
      )}
    </>
  )
}

PrintablePurchaseFull.propTypes = {
  printInboundTicketData: PropTypes.object.isRequired,
  ticketCreator: PropTypes.object.isRequired,
  paymentData: PropTypes.object,
  customerData: PropTypes.object,
  vehicleData: PropTypes.object,
  pricePrepend: PropTypes.string,
}

PrintablePurchaseFull.defaultProps = {
  paymentData: {},
  customerData: {},
  vehicleData: {},
  pricePrepend: '$',
}

export default PrintablePurchaseFull
